import React, { useCallback, useEffect, useRef } from 'react';
import { useStorm } from './StormContext';

// Typing rhythm → storm, without a React render per keystroke. Keystrokes only bump a ref;
// a 250ms ticker turns "keys in the last ~2s" into a 0..1 level, eases toward it, decays
// during pauses, and pushes to the storm only when it moved by more than 0.02.
const WINDOW_MS = 2000;
const KEYS_FOR_MAX = 14; // ~7 keys/sec reads as a burst
const TICK_MS = 250;

// levelRef: the live 0..1 rhythm level (read it from event handlers; it never re-renders).
export function useTypingRhythm(): { onKey: () => void; levelRef: React.MutableRefObject<number> } {
  const { setRhythm, reducedMotion } = useStorm();
  const stamps = useRef<number[]>([]);
  const level = useRef(0);
  const sent = useRef(0);

  const onKey = useCallback(() => {
    const now = performance.now();
    stamps.current.push(now);
    if (stamps.current.length > 64) stamps.current.splice(0, stamps.current.length - 64);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const t = window.setInterval(() => {
      const now = performance.now();
      stamps.current = stamps.current.filter((s) => now - s < WINDOW_MS);
      const targetLevel = Math.min(1, stamps.current.length / KEYS_FOR_MAX);
      const k = targetLevel > level.current ? 0.45 : 0.12; // rise fast, breathe down slowly
      level.current += (targetLevel - level.current) * k;
      if (Math.abs(level.current - sent.current) > 0.02) { sent.current = level.current; setRhythm(level.current); }
    }, TICK_MS);
    return () => { window.clearInterval(t); setRhythm(0); };
  }, [reducedMotion, setRhythm]);

  return { onKey, levelRef: level };
}
